import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Shield, Zap, Globe, TrendingUp, Bot, Brain, CreditCard, Handshake, Package, ChevronRight, Users, Factory } from 'lucide-react';
import SEO from './SEO';
import ProblemSolution from './ProblemSolution';
import RagTechnology from './RagTechnology';

// EQTOS Assets
const EQTOS_LOGO = '/assets/logo-eqtos.png';
const EQTOS_FAVICON = '/favicon-eqtos.png';

const EqtosLanding = ({ lang, setLang }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    profile: 'user'
  });
  const [submitted, setSubmitted] = useState(false);

  // SEO content per language
  const seoContent = {
    en: {
      title: 'EQTOS | AI-Powered Trade Finance Platform',
      description: 'Secure, transparent global trade powered by AI Agents. Connect Latin American exporters with buyers in North America, Asia, and Europe. Join the waitlist for the future of logistics and finance.'
    },
    es: {
      title: 'EQTOS | Plataforma de Comercio con IA',
      description: 'Comercio global seguro y transparente impulsado por Agentes de IA. Conecta exportadores latinoamericanos con compradores en Norteamérica, Asia y Europa. Únete a la lista de espera.'
    }
  };

  const seo = seoContent[lang] || seoContent.en;

  // Full translation dictionary
  const content = {
    en: {
      // Navigation
      backHome: 'Back to Samanext',
      
      // Hero
      badge: 'COGNITIVE TRADE OS',
      headline: 'The Future of\nGlobal Trade',
      subheadline: 'AI-powered platform connecting Latin American exporters with buyers in North America ($1.1T), Asia ($500B), and Europe ($300B).',
      
      // Stats
      stats: [
        { value: '$1.9T', label: 'Market Opportunity' },
        { value: '5', label: 'AI Agents' },
        { value: '24/7', label: 'Operations' }
      ],
      
      // AI Agents Section
      agentsTitle: 'The AI Agents',
      agentsSubtitle: 'Five specialized cognitive agents working in perfect synchronization to automate and secure your trade operations.',
      agents: [
        {
          id: 'sentinel',
          name: 'Sentinel',
          role: 'Security Agent',
          description: 'Real-time fraud detection and compliance monitoring. Protects every transaction with military-grade security.',
          icon: Shield
        },
        {
          id: 'copilot',
          name: 'Copilot',
          role: 'Assistant Agent',
          description: 'Your 24/7 trade advisor. Answers questions, generates reports, and guides you through complex operations.',
          icon: Bot
        },
        {
          id: 'credit',
          name: 'Credit',
          role: 'Risk Agent',
          description: 'Instant creditworthiness assessment. Unlocks smart financing options for qualified exporters.',
          icon: CreditCard
        },
        {
          id: 'matching',
          name: 'Matching',
          role: 'Trade Agent',
          description: 'Cognitive algorithms that find optimal buyer-seller matches based on history, preferences, and market conditions.',
          icon: Handshake
        },
        {
          id: 'customs',
          name: 'Customs',
          role: 'Logistics Agent',
          description: 'Automated documentation, tariff calculations, and regulatory compliance for seamless cross-border operations.',
          icon: Package
        }
      ],
      
      // Ecosystem Section
      ecosystemTitle: 'Ecosystem Roles',
      ecosystemSubtitle: 'Two sides of the same coin. EQTOS creates value for both producers and buyers.',
      producers: {
        title: 'Producers',
        subtitle: 'Exporters & Manufacturers',
        benefits: [
          'Direct access to global buyers',
          'Smart financing & early payments',
          'Automated export documentation',
          'Real-time shipment tracking',
          'Price optimization algorithms'
        ]
      },
      buyers: {
        title: 'Buyers',
        subtitle: 'Importers & Distributors',
        benefits: [
          'End-to-end traceability',
          'Smart contracts & escrow',
          'Verified supplier network',
          'Quality assurance protocols',
          'Consolidated logistics'
        ]
      },
      
      // Pilot Program Form
      waitlist: 'Apply for the Pilot Program',
      waitlistSub: 'We are selecting producers and exporters to test our Beta technology. Help us build the future of trade.',
      namePlaceholder: 'Your Name',
      emailPlaceholder: 'your@email.com',
      profileLabel: 'I am a...',
      investor: 'Investor',
      user: 'Exporter / Buyer',
      partner: 'Strategic Partner',
      submit: 'Apply to Pilot',
      submitted: "Application Received!",
      submittedSub: "We'll review your profile and contact you soon.",
      
      // Features
      features: [
        { icon: Shield, title: 'Secure Transactions', desc: 'End-to-end encrypted trade operations' },
        { icon: Zap, title: 'AI-Powered Matching', desc: 'Cognitive algorithms find optimal partners' },
        { icon: Globe, title: 'Global Reach', desc: 'Access markets across 3 continents' },
        { icon: TrendingUp, title: 'Real-time Analytics', desc: 'Track and optimize your trade flows' }
      ],
      
      // Footer
      footerText: '© 2025 EQTOS by Samanext Technologies, LLC'
    },
    
    es: {
      // Navegación
      backHome: 'Volver a Samanext',
      
      // Hero
      badge: 'SISTEMA OPERATIVO DE COMERCIO COGNITIVO',
      headline: 'El Futuro del\nComercio Global',
      subheadline: 'Plataforma impulsada por IA que conecta exportadores latinoamericanos con compradores en Norteamérica ($1.1T), Asia ($500B) y Europa ($300B).',
      
      // Stats
      stats: [
        { value: '$1.9T', label: 'Oportunidad de Mercado' },
        { value: '5', label: 'Agentes IA' },
        { value: '24/7', label: 'Operaciones' }
      ],
      
      // Sección Agentes IA
      agentsTitle: 'Los Agentes de IA',
      agentsSubtitle: 'Cinco agentes cognitivos especializados trabajando en perfecta sincronización para automatizar y asegurar tus operaciones comerciales.',
      agents: [
        {
          id: 'sentinel',
          name: 'Sentinel',
          role: 'Agente de Seguridad',
          description: 'Detección de fraude en tiempo real y monitoreo de cumplimiento. Protege cada transacción con seguridad de grado militar.',
          icon: Shield
        },
        {
          id: 'copilot',
          name: 'Copilot',
          role: 'Agente Asistente',
          description: 'Tu asesor comercial 24/7. Responde preguntas, genera informes y te guía en operaciones complejas.',
          icon: Bot
        },
        {
          id: 'credit',
          name: 'Credit',
          role: 'Agente de Riesgo',
          description: 'Evaluación instantánea de solvencia. Desbloquea opciones de financiamiento inteligente para exportadores calificados.',
          icon: CreditCard
        },
        {
          id: 'matching',
          name: 'Matching',
          role: 'Agente Comercial',
          description: 'Algoritmos cognitivos que encuentran coincidencias óptimas comprador-vendedor basadas en historial, preferencias y condiciones del mercado.',
          icon: Handshake
        },
        {
          id: 'customs',
          name: 'Customs',
          role: 'Agente de Logística',
          description: 'Documentación automatizada, cálculos arancelarios y cumplimiento regulatorio para operaciones transfronterizas sin fricciones.',
          icon: Package
        }
      ],
      
      // Sección Ecosistema
      ecosystemTitle: 'Roles del Ecosistema',
      ecosystemSubtitle: 'Dos caras de la misma moneda. EQTOS crea valor tanto para productores como para compradores.',
      producers: {
        title: 'Productores',
        subtitle: 'Exportadores y Fabricantes',
        benefits: [
          'Acceso directo a compradores globales',
          'Financiamiento inteligente y pagos anticipados',
          'Documentación de exportación automatizada',
          'Seguimiento de envíos en tiempo real',
          'Algoritmos de optimización de precios'
        ]
      },
      buyers: {
        title: 'Compradores',
        subtitle: 'Importadores y Distribuidores',
        benefits: [
          'Trazabilidad de extremo a extremo',
          'Contratos inteligentes y custodia',
          'Red de proveedores verificados',
          'Protocolos de aseguramiento de calidad',
          'Logística consolidada'
        ]
      },
      
      // Formulario Programa Piloto
      waitlist: 'Solicita Acceso al Programa Piloto',
      waitlistSub: 'Estamos seleccionando productores y exportadores para probar nuestra tecnología Beta. Ayúdanos a construir el futuro del comercio.',
      namePlaceholder: 'Tu Nombre',
      emailPlaceholder: 'tu@email.com',
      profileLabel: 'Soy un...',
      investor: 'Inversionista',
      user: 'Exportador / Comprador',
      partner: 'Socio Estratégico',
      submit: 'Aplicar al Piloto',
      submitted: '¡Solicitud Recibida!',
      submittedSub: 'Revisaremos tu perfil y te contactaremos pronto.',
      
      // Features
      features: [
        { icon: Shield, title: 'Transacciones Seguras', desc: 'Operaciones comerciales cifradas de extremo a extremo' },
        { icon: Zap, title: 'Matching con IA', desc: 'Algoritmos cognitivos encuentran socios óptimos' },
        { icon: Globe, title: 'Alcance Global', desc: 'Accede a mercados en 3 continentes' },
        { icon: TrendingUp, title: 'Análisis en Tiempo Real', desc: 'Rastrea y optimiza tus flujos comerciales' }
      ],
      
      // Footer
      footerText: '© 2025 EQTOS por Samanext Technologies, LLC'
    }
  };

  const t = content[lang] || content.en;

  const toggleLang = () => {
    if (setLang) {
      setLang(lang === 'en' ? 'es' : 'en');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-black relative w-full overflow-x-hidden">
      {/* Dynamic Favicon for EQTOS */}
      <Helmet>
        <link rel="icon" type="image/png" href={EQTOS_FAVICON} />
        <link rel="apple-touch-icon" href={EQTOS_FAVICON} />
      </Helmet>
      
      {/* SEO Meta Tags */}
      <SEO 
        title={seo.title}
        description={seo.description}
        image="https://customer-assets.emergentagent.com/job_4a49ee36-674e-4876-bdde-a21aaf32773b/artifacts/eun4u6t9_Logo-EQTOS.png"
        url="https://samanext.com/eqtos"
        locale={lang === 'es' ? 'es_ES' : 'en_US'}
      />
      
      {/* Cyberpunk Grid Background - hidden on mobile for performance */}
      <div className="absolute inset-0 hidden sm:block overflow-hidden pointer-events-none">
        <div 
          className="absolute inset-0 opacity-[0.15]"
          style={{
            backgroundImage: `
              linear-gradient(to right, #00FFD1 1px, transparent 1px),
              linear-gradient(to bottom, #00FFD1 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-[#00FFD1]/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#00FFD1]/5 rounded-full blur-[120px]" />
        <div 
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,209,0.1) 2px, rgba(0,255,209,0.1) 4px)'
          }}
        />
      </div>

      {/* Navigation Bar */}
      <nav className="relative z-50 border-b border-[#00FFD1]/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3 text-white hover:text-[#00FFD1] transition-colors">
            <ChevronRight className="w-4 h-4 rotate-180" />
            <span className="text-sm font-medium">{t.backHome}</span>
          </a>
          
          {/* EQTOS Logo */}
          <img 
            src={EQTOS_LOGO} 
            alt="EQTOS Logo" 
            className="h-8 md:h-10 w-auto"
          />
          
          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium border border-[#00FFD1]/30 hover:border-[#00FFD1] transition-all duration-300"
          >
            <span className={lang === 'en' ? 'text-[#00FFD1]' : 'text-white/60'}>EN</span>
            <span className="text-white/40">|</span>
            <span className={lang === 'es' ? 'text-[#00FFD1]' : 'text-white/60'}>ES</span>
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-16">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Content */}
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00FFD1]/40 bg-[#00FFD1]/5 mb-8">
                  <span className="w-2 h-2 bg-[#00FFD1] rounded-full animate-pulse" />
                  <span className="text-[#00FFD1] text-xs font-semibold tracking-[0.2em]">{t.badge}</span>
                </div>

                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6 whitespace-pre-line">
                  {t.headline}
                </h1>

                <p className="text-lg text-white/70 leading-relaxed mb-10 max-w-xl">
                  {t.subheadline}
                </p>

                <div className="flex gap-8 mb-10">
                  {t.stats.map((stat, idx) => (
                    <div key={idx} className="text-center">
                      <div className="text-2xl md:text-3xl font-bold text-[#00FFD1]">{stat.value}</div>
                      <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Waitlist Form - Dashboard Style */}
              <div className="relative">
                <div className="bg-black/80 border border-[#00FFD1]/30 backdrop-blur-sm">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-[#00FFD1]/20">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-2">
                        <span className="w-3 h-3 rounded-full bg-red-500/80" />
                        <span className="w-3 h-3 rounded-full bg-yellow-500/80" />
                        <span className="w-3 h-3 rounded-full bg-green-500/80" />
                      </div>
                      <span className="text-[#00FFD1]/60 text-xs font-mono ml-4">eqtos://access-portal</span>
                    </div>
                    <div className="text-[#00FFD1] text-xs font-mono">v2.0.1</div>
                  </div>

                  <div className="p-8">
                    {!submitted ? (
                      <>
                        <h3 className="text-xl font-semibold text-white mb-2">{t.waitlist}</h3>
                        <p className="text-white/50 text-sm mb-6">{t.waitlistSub}</p>
                        
                        <form 
                          name="eqtos-pilot-program" 
                          method="POST" 
                          data-netlify="true"
                          netlify-honeypot="bot-field"
                          onSubmit={handleSubmit}
                          className="space-y-4"
                        >
                          <input type="hidden" name="form-name" value="eqtos-pilot-program" />
                          <input type="hidden" name="bot-field" />
                          
                          <div>
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder={t.namePlaceholder}
                              required
                              className="w-full bg-black/50 border border-[#00FFD1]/30 px-4 py-3 text-white placeholder-white/30 focus:border-[#00FFD1] focus:outline-none transition-colors font-mono"
                            />
                          </div>

                          <div>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder={t.emailPlaceholder}
                              required
                              className="w-full bg-black/50 border border-[#00FFD1]/30 px-4 py-3 text-white placeholder-white/30 focus:border-[#00FFD1] focus:outline-none transition-colors font-mono"
                            />
                          </div>

                          <div>
                            <label className="text-white/50 text-xs uppercase tracking-wider mb-2 block">{t.profileLabel}</label>
                            <select
                              name="profile"
                              value={formData.profile}
                              onChange={handleChange}
                              className="w-full bg-black/50 border border-[#00FFD1]/30 px-4 py-3 text-white focus:border-[#00FFD1] focus:outline-none transition-colors font-mono appearance-none cursor-pointer"
                            >
                              <option value="user">{t.user}</option>
                              <option value="investor">{t.investor}</option>
                              <option value="partner">{t.partner}</option>
                            </select>
                          </div>

                          <button
                            type="submit"
                            className="w-full bg-[#00FFD1] text-black py-4 font-semibold hover:bg-[#00FFD1]/90 transition-colors flex items-center justify-center gap-2 group mt-6"
                          >
                            {t.submit}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </button>
                        </form>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 border-2 border-[#00FFD1] rounded-full flex items-center justify-center">
                          <Zap className="w-8 h-8 text-[#00FFD1]" />
                        </div>
                        <h3 className="text-2xl font-bold text-[#00FFD1] mb-2">{t.submitted}</h3>
                        <p className="text-white/60">{t.submittedSub}</p>
                      </div>
                    )}
                  </div>

                  <div className="px-6 py-3 border-t border-[#00FFD1]/20 flex items-center justify-between">
                    <span className="text-[#00FFD1]/40 text-xs font-mono">STATUS: ACCEPTING_EARLY_ACCESS</span>
                    <span className="w-2 h-2 bg-[#00FFD1] rounded-full animate-pulse" />
                  </div>
                </div>

                <div className="absolute -top-4 -right-4 w-8 h-8 border-t-2 border-r-2 border-[#00FFD1]/40" />
                <div className="absolute -bottom-4 -left-4 w-8 h-8 border-b-2 border-l-2 border-[#00FFD1]/40" />
              </div>
            </div>
          </div>
        </section>

        {/* Market Problem & Solution Section */}
        <ProblemSolution lang={lang} />

        {/* AI Agents Section */}
        <section className="py-20 border-t border-[#00FFD1]/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-16">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00FFD1]/40 bg-[#00FFD1]/5 mb-6">
                <Brain className="w-4 h-4 text-[#00FFD1]" />
                <span className="text-[#00FFD1] text-xs font-semibold tracking-[0.2em]">COGNITIVE CORE</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t.agentsTitle}
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                {t.agentsSubtitle}
              </p>
            </div>

            {/* Agents Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.agents.map((agent, idx) => (
                <div 
                  key={agent.id}
                  className={`
                    relative p-6 border bg-black/50 hover:bg-black/80 transition-all duration-300 group
                    ${idx === 0 ? 'border-[#00FFD1]/40 lg:col-span-1' : 'border-white/10 hover:border-[#00FFD1]/30'}
                  `}
                >
                  {/* Agent Icon */}
                  <div className={`
                    w-12 h-12 flex items-center justify-center mb-4 border
                    ${idx === 0 ? 'border-[#00FFD1] bg-[#00FFD1]/10' : 'border-white/20 group-hover:border-[#00FFD1]/50'}
                    transition-colors
                  `}>
                    <agent.icon className={`w-6 h-6 ${idx === 0 ? 'text-[#00FFD1]' : 'text-white/60 group-hover:text-[#00FFD1]'} transition-colors`} />
                  </div>

                  {/* Agent Info */}
                  <div className="flex items-center gap-3 mb-3">
                    <h3 className="text-xl font-bold text-white">{agent.name}</h3>
                    <span className="text-[#00FFD1]/60 text-xs font-mono px-2 py-1 bg-[#00FFD1]/5 border border-[#00FFD1]/20">
                      {agent.role}
                    </span>
                  </div>

                  <p className="text-white/50 text-sm leading-relaxed">
                    {agent.description}
                  </p>

                  {/* Corner accent */}
                  <div className="absolute top-0 right-0 w-4 h-4 border-t border-r border-[#00FFD1]/20" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ecosystem Roles Section */}
        <section className="py-20 border-t border-[#00FFD1]/10 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 lg:px-16">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#00FFD1]/40 bg-[#00FFD1]/5 mb-6">
                <Users className="w-4 h-4 text-[#00FFD1]" />
                <span className="text-[#00FFD1] text-xs font-semibold tracking-[0.2em]">ECOSYSTEM</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                {t.ecosystemTitle}
              </h2>
              <p className="text-white/60 max-w-2xl mx-auto">
                {t.ecosystemSubtitle}
              </p>
            </div>

            {/* Two Columns */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Producers Column */}
              <div className="relative p-8 border border-[#00FFD1]/30 bg-black/50">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/40">
                    <Factory className="w-7 h-7 text-[#00FFD1]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#00FFD1]">{t.producers.title}</h3>
                    <p className="text-white/50 text-sm">{t.producers.subtitle}</p>
                  </div>
                </div>

                <ul className="space-y-4">
                  {t.producers.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30 mt-0.5">
                        <span className="text-[#00FFD1] text-xs">✓</span>
                      </div>
                      <span className="text-white/80">{benefit}</span>
                    </li>
                  ))}
                </ul>

                {/* Corner accents */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#00FFD1]/40" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#00FFD1]/40" />
              </div>

              {/* Buyers Column */}
              <div className="relative p-8 border border-white/20 bg-black/50 hover:border-[#00FFD1]/30 transition-colors">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-white/5 border border-white/20">
                    <TrendingUp className="w-7 h-7 text-white/70" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-white">{t.buyers.title}</h3>
                    <p className="text-white/50 text-sm">{t.buyers.subtitle}</p>
                  </div>
                </div>

                <ul className="space-y-4">
                  {t.buyers.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center bg-white/5 border border-white/20 mt-0.5">
                        <span className="text-white/60 text-xs">✓</span>
                      </div>
                      <span className="text-white/70">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 border-t border-[#00FFD1]/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-16">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {t.features.map((feature, idx) => (
                <div 
                  key={idx}
                  className="p-6 border border-[#00FFD1]/20 bg-black/50 hover:border-[#00FFD1]/40 transition-colors group"
                >
                  <feature.icon className="w-8 h-8 text-[#00FFD1]/60 group-hover:text-[#00FFD1] transition-colors mb-4" />
                  <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                  <p className="text-white/50 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* RAG Technology Section */}
        <RagTechnology lang={lang} />
      </main>

      {/* Footer */}
      <footer className="relative z-10 border-t border-[#00FFD1]/20 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-16 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">{t.footerText}</p>
          <a href="https://eqtos.tech" target="_blank" rel="noopener noreferrer" className="text-[#00FFD1] text-sm hover:underline">
            eqtos.tech
          </a>
        </div>
      </footer>
    </div>
  );
};

export default EqtosLanding;
